import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import Partenaires from "../Partenaires/Partenaires.jsx";
import { nationLogo, bamadaLogo, somacouLogo, dgiLogo, def1, def2, def3, def4, def5, def6, def7, def8, def9 } from '../../assets/images';
import { X, Building, FileText, Check, ArrowUpRight } from 'lucide-react';

const getReferences = (t) => [
  {
    nom: t('references.ref1.nom'),
    logo: nationLogo,
    description: t('references.ref1.desc'),
    detailledDescription: t('references.ref1.detailed'),
    travauxType: t('references.ref1.type'),
    travaux: [t('references.ref1.t1'), t('references.ref1.t2'), t('references.ref1.t3'), t('references.ref1.t4'), t('references.ref1.t5')],
    year: "2019-Présent"
  },
  {
    nom: t('references.ref2.nom'),
    logo: bamadaLogo,
    description: t('references.ref2.desc'),
    detailledDescription: t('references.ref2.detailed'),
    travauxType: t('references.ref2.type'),
    travaux: [t('references.ref2.t1'), t('references.ref2.t2'), t('references.ref2.t3')],
    year: "2018-Présent"
  },
  {
    nom: t('references.ref3.nom'),
    logo: dgiLogo,
    description: t('references.ref3.desc'),
    detailledDescription: t('references.ref3.detailed'),
    travauxType: t('references.ref3.type'),
    travaux: [t('references.ref3.t1')],
    year: "2019-Présent"
  },
  {
    nom: t('references.ref4.nom'),
    logo: somacouLogo,
    description: t('references.ref4.desc'),
    detailledDescription: t('references.ref4.detailed'),
    travauxType: t('references.ref4.type'),
    travaux: [t('references.ref4.t1'), t('references.ref4.t2'), t('references.ref4.t3')],
    year: "2017-Présent"
  },
];

const References = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');
  const bgSlides = [def1, def2, def3, def4, def5, def6, def7, def8, def9];
  const [bgSlide, setBgSlide] = useState(4); // Start at def5
  const [selected, setSelected] = useState(null);

  const references = getReferences(t);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgSlide((prev) => (prev + 1) % bgSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bgSlides.length]);

  return (
    <section id="references" className="section-padding bg-auxis-off-white dark:bg-auxis-charcoal relative overflow-hidden transition-colors duration-300">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img 
            key={bgSlide}
            src={bgSlides[bgSlide]} 
            alt={`AUXIS Background ${bgSlide + 1}`}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-auxis-charcoal/90 via-auxis-charcoal/70 to-auxis-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal via-transparent to-auxis-charcoal/30" />
      </div>

      <div className="container-wide relative z-10">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-flex items-center gap-2.5 text-caption uppercase font-bold tracking-[0.15em] text-auxis-gold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-auxis-gold" />
              {t('references.tag')}
            </span>
            <h2 className="text-display text-white text-center mb-5">{t('references.title')}</h2>
            <div className="gold-line mx-auto mb-6" />
            <p className="text-body-lg text-gray-300 max-w-2xl mx-auto text-center">
              {t('references.subtitle')}
            </p>
          </motion.div>
        </div>

        {/* Reference cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-16">
          {references.map((ref, index) => (
            <motion.div
              key={ref.nom}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setSelected(ref)}
              className="group cursor-pointer p-5 md:p-6 text-center relative overflow-hidden bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 shadow-sm hover:shadow-md hover:shadow-auxis-gold/20 transition-all duration-300"
            >
              {/* Hover glow */}
              <div className="absolute inset-0 bg-gradient-to-b from-auxis-gold/[0.15] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <div className="aspect-[3/2] bg-white/90 rounded-xl mb-4 flex items-center justify-center overflow-hidden group-hover:bg-white border border-white/30 group-hover:border-auxis-gold/50 transition-all duration-500">
                  <img src={ref.logo} alt={ref.nom} className="w-20 h-20 md:w-24 md:h-24 object-contain transition-transform duration-500 group-hover:scale-110" />
                </div>
                <h3 className="text-sm md:text-base font-semibold text-white mb-1 group-hover:text-auxis-gold transition-colors duration-300">{ref.nom}</h3>
                <p className="text-xs text-gray-300">{ref.description}</p>
                
                {/* Arrow icon on hover */}
                <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ArrowUpRight size={16} className="mx-auto text-auxis-gold" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Partenaires */}
        <Partenaires />

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-body-sm text-gray-300 italic mt-12"
        >
          {t('references.tagline')}
        </motion.p>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-auxis-charcoal/60 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="bg-white dark:bg-auxis-charcoal border border-gray-100 dark:border-white/10 rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b border-gray-100 dark:border-white/10">
                <div className="h-0.5 bg-gradient-to-r from-auxis-gold to-auxis-gold-light" />
                <div className="p-5 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-auxis-charcoal dark:text-white">{selected.nom}</h3>
                  <button onClick={() => setSelected(null)} className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-auxis-charcoal-light text-gray-500 dark:text-gray-400 transition-colors">
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto flex-grow p-6 space-y-6">
                {/* Logo */}
                <div className="flex justify-center p-6 bg-gray-50 dark:bg-white/90 rounded-xl border border-gray-100 dark:border-white/10">
                  <img src={selected.logo} alt={selected.nom} className="h-24 object-contain" />
                </div>

                {/* About */}
                <div>
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-white/10">
                    <Building size={16} className="text-auxis-gold" />
                    <h4 className="text-sm font-semibold text-auxis-charcoal dark:text-white">{t('references.about')}</h4>
                  </div>
                  <p className="text-body-sm text-gray-600 dark:text-gray-300 leading-relaxed">{selected.detailledDescription}</p>
                </div>

                {/* Works */}
                <div>
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-white/10">
                    <FileText size={16} className="text-auxis-gold" />
                    <h4 className="text-sm font-semibold text-auxis-charcoal dark:text-white">{selected.travauxType}</h4>
                  </div>
                  <ul className="space-y-2.5">
                    {selected.travaux.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <Check size={14} className="text-auxis-gold mt-0.5 flex-shrink-0" />
                        <span className="text-body-sm text-gray-600 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="border-t border-gray-100 dark:border-white/10 p-4 bg-gray-50 dark:bg-auxis-charcoal-light">
                <button onClick={() => setSelected(null)} className="w-full py-2.5 bg-auxis-gold text-white text-sm font-bold rounded-xl hover:shadow-lg transition-all duration-300">
                  {t('references.close')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default References;